import { z } from 'zod';
import { garmentTypeKeySchema } from '../constants/garment-types.js';
import { workflowStageSchema } from '../constants/stages.js';
import { positiveMilliUnitsSchema, stockUnitSchema } from '../inventory/units.js';
import { gstRateSchema, hsnSacSchema } from '../money/gst.js';
import { nonNegativePaiseSchema } from '../money/paise.js';
import {
  dateInputSchema,
  isoDateStringSchema,
  objectIdSchema,
  paginationQuerySchema,
} from './common.js';
import { couponCodeSchema } from './coupons.js';
import { measurementSnapshotSchema } from './measurement-profiles.js';
import { bookingAdvanceSchema } from './payments.js';

/**
 * The central transaction of the product.
 *
 * A customer walks in with three blouses and a lehenga on one bill. The ORDER is the bill; the
 * ITEM is the garment. Almost everything downstream — who stitches it, what stage it is at,
 * when it is promised, what it earned — hangs off the item, not the bill, because those three
 * blouses go to one karigar and the lehenga to another and they finish on different days.
 *
 * Two things in this file are load-bearing.
 *
 * The first is `measurements` on the item: a FROZEN COPY of the customer's measurement profile,
 * not a reference to it. A customer's body changes and a shop re-measures; a completed order
 * must remain provable in a dispute. `sourceProfileId` records where the copy came from, which
 * is a trail, not a live link.
 *
 * The second is that no total is an input. `subtotalPaise`, `totalPaise` and `balancePaise` are
 * computed by the server from the items and are absent from every request shape below — a
 * client that wants a different total has to change a price, which is a priced, audited thing.
 */

export const ORDER_NOTES_MAX_LENGTH = 2000;
export const CANCEL_REASON_MAX_LENGTH = 500;

/** Who supplied the cloth. It changes the price and who is blamed if it is short. */
export const FABRIC_SOURCES = ['customer', 'shop'] as const;
export type FabricSource = (typeof FABRIC_SOURCES)[number];
export const fabricSourceSchema = z.enum(FABRIC_SOURCES);

export const fabricSchema = z.object({
  source: fabricSourceSchema,
  description: z.string().nullable(),
  /** Metres of cloth. Not money, so a plain number. */
  metres: z.number().nonnegative().nullable(),
  /** Rate per metre, charged only when the shop supplied the cloth. */
  ratePerMetrePaise: nonNegativePaiseSchema.nullable(),
});
export type Fabric = z.infer<typeof fabricSchema>;

export const fabricInputSchema = z.object({
  source: fabricSourceSchema.default('customer'),
  description: z.string().trim().max(200).nullish(),
  metres: z.number().nonnegative().max(1000).nullish(),
  ratePerMetrePaise: nonNegativePaiseSchema.nullish(),
});

/**
 * The maximum extras on one garment. Twenty is far past "piping, lining and a zip" and short
 * enough that a payload cannot be used to make one order line expensive to price.
 */
export const MAX_ADD_ONS_PER_ITEM = 20;

/**
 * One priced extra on a garment — piping, lining, a fancy button, a fall.
 *
 * WHAT IS SENT IS A PRODUCT AND A QUANTITY, never a price. The rate comes from the shop's own
 * catalogue at booking time, exactly as a garment's does not come from the browser either:
 * a client that could name its own price would make the `orders.pricing.write` permission
 * decorative.
 */
export const orderItemAddOnInputSchema = z.object({
  /** An `accessory` product from the shop's catalogue. */
  productId: objectIdSchema,
  quantity: z.number().int().positive().max(99).default(1),
});
export type OrderItemAddOnInput = z.input<typeof orderItemAddOnInputSchema>;

/**
 * An add-on as it is stored on the garment, and as every screen reads it.
 *
 * A FROZEN SNAPSHOT, like the measurements beside it. The name and the rate are copied at
 * booking and never followed back to the catalogue: a shop that puts its piping up next month
 * has not thereby changed what it quoted this customer, and a bill that silently re-priced
 * itself between booking and collection is the one thing a customer will always notice.
 */
export const orderItemAddOnSchema = z.object({
  productId: objectIdSchema,
  /** What it was called when it was added. */
  name: z.string(),
  quantity: z.number().int().positive(),
  unitPricePaise: nonNegativePaiseSchema,
  /** `unitPricePaise * quantity`. Server-computed, like every other total. */
  linePaise: nonNegativePaiseSchema,
});
export type OrderItemAddOn = z.infer<typeof orderItemAddOnSchema>;

// --- Product lines -----------------------------------------------------------------------------

/** Far past a counter sale's basket, short enough that one payload cannot be made expensive. */
export const MAX_PRODUCT_LINES_PER_ORDER = 50;
export const PRODUCT_LINE_NAME_MAX_LENGTH = 120;

/**
 * One thing sold over the counter on this bill — two metres of fabric, a readymade blouse, a
 * packet of buttons. A LINE OF THE ORDER, not of a garment: it has no measurements, no karigar
 * and no stage, and it goes out with the order rather than with any one garment. (Extras that
 * belong to a garment — its piping, its lining — are that garment's `addOns`.)
 *
 * The PRICE is sent, like a garment's: the counter may sell at other than the list price, and
 * `orders.pricing.write` is what lets it. Everything ELSE about a catalogue line — its name, its
 * unit, its GST rate, its HSN code, the stock it draws down — comes from the catalogue at booking
 * and is frozen there. A line with no `productId` is typed by hand, and carries the unit and tax
 * the counter gave it, or the shop's defaults.
 *
 * `quantity` is in MILLI-UNITS, the inventory module's representation: 2.5 metres is 2500 and one
 * piece is 1000. A decimal would drift as soon as it met the stock ledger.
 */
export const orderProductLineInputSchema = z
  .object({
    productId: objectIdSchema.nullish(),
    name: z.string().trim().min(1).max(PRODUCT_LINE_NAME_MAX_LENGTH).nullish(),
    unit: stockUnitSchema.optional(),
    quantity: positiveMilliUnitsSchema.max(10_000_000, 'That is more than a shop sells at once'),
    unitPricePaise: nonNegativePaiseSchema,
    gstRatePercent: gstRateSchema.nullish(),
    hsnCode: hsnSacSchema.nullish(),
  })
  .refine((line) => line.productId != null || line.name != null, {
    message: 'Name the product',
    path: ['name'],
  });
export type OrderProductLineInput = z.input<typeof orderProductLineInputSchema>;

/** A product line as stored — a FROZEN SNAPSHOT, like an add-on. */
export const orderProductLineSchema = z.object({
  id: objectIdSchema,
  /** Position on the bill, so lines read in the order they were added. */
  position: z.number().int().nonnegative(),
  /** The catalogue product it was copied from, or `null` for a line typed by hand. */
  productId: objectIdSchema.nullable(),
  name: z.string(),
  unit: stockUnitSchema,
  /** Milli-units. */
  quantity: positiveMilliUnitsSchema,
  unitPricePaise: nonNegativePaiseSchema,
  /** `round(quantity * unitPricePaise / 1000)`. Server-computed. */
  linePaise: nonNegativePaiseSchema,
  gstRatePercent: gstRateSchema.nullable(),
  hsnCode: z.string().nullable(),
  /** The stock variant this sale drew down, or both `null`. */
  stockItemId: z.string().nullable(),
  stockVariantId: z.string().nullable(),
});
export type OrderProductLine = z.infer<typeof orderProductLineSchema>;

/**
 * What a sale draws from a shelf that did not hold enough. The sale went through — a counter is
 * never stopped from billing over a count that is often wrong — and this says so.
 */
export const stockWarningSchema = z.object({
  stockItemId: z.string(),
  stockVariantId: z.string(),
  name: z.string(),
  /** Milli-units left on the shelf after the sale; negative is the point of the warning. */
  balanceAfter: z.number().int(),
});
export type StockWarning = z.infer<typeof stockWarningSchema>;

export const orderItemSchema = z.object({
  id: objectIdSchema,
  orderId: objectIdSchema,
  /** Position on the bill, so the items read in the order they were booked. */
  lineNumber: z.number().int().positive(),
  garmentTypeKey: z.string(),
  garmentTypeLabel: z.string(),
  quantity: z.number().int().positive(),
  fabric: fabricSchema,
  /** Per garment, before quantity. */
  unitPricePaise: nonNegativePaiseSchema,
  /**
   * `unitPricePaise * quantity`, plus fabric when the shop supplied it, plus every add-on.
   * Server-computed.
   */
  linePaise: nonNegativePaiseSchema,
  /** The frozen extras on this garment. Defaulted, so an order booked before this still parses. */
  addOns: z.array(orderItemAddOnSchema).default([]),
  /** This garment's own promised date, which need not be the order's. */
  promisedDate: isoDateStringSchema.nullable(),
  stage: workflowStageSchema,
  notes: z.string().nullable(),
  /**
   * The frozen measurements. `null` for a garment that needs none (a saree fall taken on the
   * spot), which is why it is nullable rather than merely optional.
   */
  measurements: measurementSnapshotSchema.nullable(),
  /** Where the snapshot was copied from. A trail, never a live link. */
  sourceProfileId: objectIdSchema.nullable(),
  /** True once the shop adjusted this item's own copy — "make this one an inch longer". */
  measurementsAdjusted: z.boolean(),
  cancelledAt: isoDateStringSchema.nullable(),
  cancelReason: z.string().nullable(),
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type OrderItem = z.infer<typeof orderItemSchema>;

export const orderSchema = z.object({
  id: objectIdSchema,
  /** Gapless per shop per financial year: `INV/2026-27/0007`. */
  orderNumber: z.string(),
  customerId: objectIdSchema,
  customerName: z.string(),
  customerPhone: z.string(),
  /**
   * The opportunity this booking came out of, or null when the counter booked it directly.
   *
   * THE POINTER LIVES ON THE ORDER rather than as an `orderId` on the deal, by the same test
   * `quotation.dealId` is argued with: one opportunity can produce several orders — the lehenga
   * booked now, the blouse in a fortnight — and an order comes out of at most one. The many side
   * holds the pointer, or the second booking silently overwrites the first one's provenance.
   *
   * PROVENANCE, NOT MONEY. Nothing reads through it to price anything; it exists so the customer
   * hub can say which opportunity became which order, and so "what did we actually sell her"
   * has an answer that survives the deal being closed.
   */
  dealId: objectIdSchema.nullable(),
  bookedAt: isoDateStringSchema,
  /** What the whole bill was promised for. Individual items may differ. */
  promisedDate: isoDateStringSchema.nullable(),
  /** Urgent work, so the list and the Kanban can lead with it. */
  priority: z.boolean(),
  notes: z.string().nullable(),
  /** The stage the order as a whole has reached — the least advanced live item. */
  stage: workflowStageSchema,
  itemCount: z.number().int().nonnegative(),
  subtotalPaise: nonNegativePaiseSchema,
  /**
   * What the shop charges to deliver this order. Its own figure rather than a line among the
   * garments: it is not stitched, it carries no measurements, and a shop that waives it wants
   * to zero one box, not delete a row and recount the bill.
   */
  deliveryChargePaise: nonNegativePaiseSchema,
  /**
   * The EFFECTIVE discount — what somebody typed plus whatever the coupon took off. This is
   * the figure the bill, the invoice and every report read, which is why adding coupons needed
   * no change to any of them.
   */
  discountPaise: nonNegativePaiseSchema,
  /** The half a person typed, so the screen can show the two apart. */
  manualDiscountPaise: nonNegativePaiseSchema.default(0),
  /** The code that was applied, or `null`. Frozen as it read at the time. */
  couponCode: z.string().nullable().default(null),
  /** What that code took off. Frozen when it was applied — see the model for why. */
  couponDiscountPaise: nonNegativePaiseSchema.default(0),
  totalPaise: nonNegativePaiseSchema,
  /** Maintained by task 17 through one service, never written by a client. */
  amountPaidPaise: nonNegativePaiseSchema,
  balancePaise: z.number().int(),
  cancelledAt: isoDateStringSchema.nullable(),
  cancelReason: z.string().nullable(),
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type Order = z.infer<typeof orderSchema>;

export const orderWithItemsSchema = orderSchema.extend({
  items: z.array(orderItemSchema),
  /** Defaulted, so a response from before product lines still parses. */
  productLines: z.array(orderProductLineSchema).default([]),
  /** Present only on the response to the write that caused them. */
  stockWarnings: z.array(stockWarningSchema).optional(),
});
export type OrderWithItems = z.infer<typeof orderWithItemsSchema>;

// --- Booking ---------------------------------------------------------------------------------

export const orderItemInputSchema = z.object({
  garmentTypeKey: garmentTypeKeySchema,
  quantity: z.number().int().positive().max(999).default(1),
  fabric: fabricInputSchema.optional(),
  unitPricePaise: nonNegativePaiseSchema,
  promisedDate: dateInputSchema.nullish(),
  notes: z.string().trim().max(1000).nullish(),
  /**
   * The measurement profile to FREEZE onto this item. The values are copied at booking;
   * nothing about this item follows the profile afterwards.
   */
  measurementProfileId: objectIdSchema.nullish(),
  /** Priced extras on this garment, resolved against the catalogue at booking. */
  addOns: z.array(orderItemAddOnInputSchema).max(MAX_ADD_ONS_PER_ITEM).optional(),
});
export type OrderItemInput = z.input<typeof orderItemInputSchema>;

export const createOrderSchema = z
  .object({
    customerId: objectIdSchema,
    /**
     * May be empty when `productLines` is not: a counter sale of fabric alone is an order with no
     * garment in it, and is delivered the moment it is booked.
     */
    items: z.array(orderItemInputSchema).max(50).default([]),
    productLines: z.array(orderProductLineInputSchema).max(MAX_PRODUCT_LINES_PER_ORDER).default([]),
    promisedDate: dateInputSchema.nullish(),
    priority: z.boolean().default(false),
    notes: z.string().trim().max(ORDER_NOTES_MAX_LENGTH).nullish(),
    /**
     * What the counter typed off the bill. NOT the effective discount: a coupon adds to this,
     * and the server works out the sum — a client that could send the total discount could
     * quietly claim a coupon it never applied.
     */
    discountPaise: nonNegativePaiseSchema.default(0),
    /**
     * A coupon code, applied at booking. Checked, priced and frozen by the server inside the
     * booking transaction, so "first hundred customers" survives two counters at once.
     */
    couponCode: couponCodeSchema.nullish(),
    /**
     * The delivery charge, and the ONLY exception to "no total is an input" other than the
     * discount. It is not derived from anything the server knows — no garment implies it and no
     * rate card prices it — so the counter is the only place it can come from.
     */
    deliveryChargePaise: nonNegativePaiseSchema.default(0),
    /** The date the order was taken. Defaults to now, and the counter may back-date a slip. */
    bookedAt: dateInputSchema.optional(),
    /**
     * The advance taken at the counter, recorded in the same transaction as the booking (task
     * 17). Optional, and the only money field a client may send — because it is money that
     * changed hands in front of them, not a total it worked out for itself.
     */
    advance: bookingAdvanceSchema.nullish(),
  })
  .refine((order) => order.items.length + order.productLines.length > 0, {
    message: 'An order needs at least one garment or product',
    path: ['items'],
  });
export type CreateOrderInput = z.input<typeof createOrderSchema>;

export const updateOrderSchema = z
  .object({
    promisedDate: dateInputSchema.nullish(),
    priority: z.boolean().optional(),
    notes: z.string().trim().max(ORDER_NOTES_MAX_LENGTH).nullish(),
    /** The typed half. A coupon on the order is applied and removed through its own routes. */
    discountPaise: nonNegativePaiseSchema.optional(),
    deliveryChargePaise: nonNegativePaiseSchema.optional(),
    /**
     * Replaces every product line on the order. Omit to leave them alone. Lines that keep their
     * `productId` and quantity move no stock; the server nets the old lines against the new.
     */
    productLines: z.array(orderProductLineInputSchema).max(MAX_PRODUCT_LINES_PER_ORDER).optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdateOrderInput = z.input<typeof updateOrderSchema>;

export const updateOrderItemSchema = z
  .object({
    quantity: z.number().int().positive().max(999).optional(),
    unitPricePaise: nonNegativePaiseSchema.optional(),
    fabric: fabricInputSchema.optional(),
    promisedDate: dateInputSchema.nullish(),
    notes: z.string().trim().max(1000).nullish(),
    /** Replaces every add-on on the garment. Omit to leave them alone. */
    addOns: z.array(orderItemAddOnInputSchema).max(MAX_ADD_ONS_PER_ITEM).optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdateOrderItemInput = z.input<typeof updateOrderItemSchema>;

/**
 * A one-off change to THIS item's frozen measurements — "make this one an inch longer".
 *
 * It writes to the item's own copy and never to the customer's profile, which is the whole
 * point: the customer's saved measurements are not wrong, this garment is just different.
 */
export const adjustItemMeasurementsSchema = z
  .object({
    adjustments: z
      .array(
        z.object({
          fieldKey: z.string().min(1),
          /** As a tailor writes it. `null` removes the value from this item's copy. */
          enteredAs: z.string().trim().max(60).nullable(),
          note: z.string().trim().max(200).nullish(),
        }),
      )
      .default([]),
    /**
     * "Redraw for this order": a NEW sketch (from `/measurement-sketches`) for this item's copy
     * alone. Explicit null takes the drawing off this item. The customer's profile is untouched.
     */
    sketchId: objectIdSchema.nullish(),
  })
  .refine((input) => input.adjustments.length > 0 || input.sketchId !== undefined, {
    message: 'Nothing to adjust',
  });
export type AdjustItemMeasurementsInput = z.input<typeof adjustItemMeasurementsSchema>;

export const cancelSchema = z.object({
  /** Required. A cancellation with no reason is the argument this field exists to end. */
  reason: z.string().trim().min(3, 'Say why this is being cancelled').max(CANCEL_REASON_MAX_LENGTH),
});
export type CancelInput = z.infer<typeof cancelSchema>;

// --- The list --------------------------------------------------------------------------------

/**
 * The filters a shop actually thinks in. Not "status = 3" — "what is due today", "what is
 * late", "what can I hand over", "who still owes me".
 */
export const ORDER_FILTERS = [
  'all',
  'due_today',
  'overdue',
  'ready',
  'unpaid',
  'in_progress',
  'delivered',
  'cancelled',
] as const;
export type OrderFilter = (typeof ORDER_FILTERS)[number];

export const ORDER_SORT_FIELDS = ['promisedDate', 'bookedAt', 'orderNumber', 'total'] as const;
export type OrderSortField = (typeof ORDER_SORT_FIELDS)[number];

export const orderSearchQuerySchema = paginationQuerySchema.extend({
  filter: z.enum(ORDER_FILTERS).default('all'),
  customerId: objectIdSchema.optional(),
  stage: workflowStageSchema.optional(),
  /** Order number or customer name/phone. */
  q: z.string().trim().max(120).optional(),
  sortBy: z.enum(ORDER_SORT_FIELDS).default('promisedDate'),
  sortDir: z.enum(['asc', 'desc']).default('asc'),
});
export type OrderSearchQuery = z.infer<typeof orderSearchQuerySchema>;

export const orderPageSchema = z.object({
  items: z.array(orderSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  /** How many orders each filter would return, for the tab counts. */
  counts: z.record(z.number().int().nonnegative()),
});
export type OrderPage = z.infer<typeof orderPageSchema>;

/**
 * The number the NEXT booking will be written under, read before it is taken.
 *
 * The booking screen shows this so the counter can say the number out loud while the customer
 * is still standing there, and write it on the slip that goes with the cloth. It is a PEEK at
 * the counter, never a reservation: the real number is drawn inside the booking transaction,
 * which is what keeps the series gapless. Two people booking at the same instant would see the
 * same figure here and only one of them would get it — so the screen that shows it must say
 * it is not taken yet.
 */
export const nextOrderNumberSchema = z.object({
  orderNumber: z.string(),
  financialYear: z.string(),
  sequence: z.number().int().positive(),
});
export type NextOrderNumber = z.infer<typeof nextOrderNumberSchema>;
